
import {Vue, Component, Prop, PropSync} from "vue-property-decorator";
import {Moment} from "moment";
import Badge from "@/components/common/BadgeNew.vue";
import BadgeColor from "@/entities/BadgeColor";
import EditTaskAction from "@/components/projects/activities/EditTaskAction.vue";
import DeleteTaskAction from "@/components/projects/activities/DeleteTask.vue";
import SuitableResources from "@/components/projects/activities/SuitableResourcesV2.vue";
import visitTypeDisplayMap from "@/entities/VisitType";
import {User, Project_Activity, Skill} from "@/generated/graphql";
import RouteNames from "@/entities/RouteNames";
import ClockIcon from "@/components/icons/ClockIcon.vue";
import CalendarIcon from "@/components/icons/LocationIconNew.vue";
import Roles from "@/entities/Roles";
import GET_PROJECT_BY_ACTIVITY_ID from "@/apollo/projectActivities/GetProjectIdByActivityId.gql";
import {hslColorFromName} from "@/utils/hslColorFromName";
import moment from "moment";
import Tooltip from "@/components/common/Tooltip.vue";
import TooltipVariants from "@/entities/TooltipVariants";
import {StyleValue} from "vue";
import SmartMatchConfigModal from "@/components/smartMatch/SmartMatchConfigModal.vue";
import {getTimezoneFromLocationString, displayTimeInLocationTimezone, deserializeTimezone} from "@/utils/timezoneUtils";
import {VUE_GOOGLEMAP_API_KEY} from "@/data/constants";

@Component({
  components: {
    ClockIcon,
    BadgeColor,
    DeleteTaskAction,
    SuitableResources,
    CalendarIcon,
    Badge,
    EditTaskAction,
    Tooltip,
    SmartMatchConfigModal,
  },
})
export default class VisitsListCard extends Vue {
  @Prop({type: Object, required: true}) readonly currentUser!: User;
  @Prop({type: Object, required: true})
  readonly activity!: Project_Activity;

  @Prop({type: String, required: true}) readonly companyId!: string;
  @PropSync("descriptionBlockHeight", {type: Number, default: undefined})
  descriptionBlockHeightValue?: number;
  @Prop({type: Array, required: false, default: () => []})
  readonly declinedMatches!: any[];
  @Prop({type: Array, required: false, default: () => []})
  readonly bookmarkedContractors!: any[];

  BadgeColor: typeof BadgeColor = BadgeColor;
  RouteNames = RouteNames;
  projectId: string = "";
  clientId: string = "";
  TooltipVariants = TooltipVariants;
  isTruncated = false;
  locationTimezone = null;
  showSmartMatchModal = false;

  async fetchClientId(): Promise<string | null> {
    try {
      const response = await this.$apollo.query({
        query: GET_PROJECT_BY_ACTIVITY_ID,
        variables: {
          activityId: this.activity?.id,
        },
      });
      return response?.data?.project[0]?.id || null;
    } catch (error) {
      console.error("Failed to fetch client ID:", error);
      return null;
    }
  }

  get isUrgent(): boolean {
    const now = new Date();
    const startTime = new Date(this.activity.start_date);

    const timeDifference = startTime.getTime() - now.getTime();

    return timeDifference > 0 && timeDifference <= 24 * 60 * 60 * 1000;
  }

  async mounted() {
    this.loadLocationTimezone();
    this.checkTruncation();
    window.addEventListener("resize", this.checkTruncation); // Re-check on resize
  }

  async loadLocationTimezone() {
    // First try to use stored timezone from activity
    if (this.activity.timezone) {

      try {
        this.locationTimezone = deserializeTimezone(this.activity.timezone);
        if (this.locationTimezone) {
          return; // Successfully loaded from stored data
        }
      } catch (error) {
        console.error("Error deserializing stored timezone:", error);
      }
    }

    // Fall back to Google Maps API if no stored timezone or deserialization failed
    if (this.location) {
      try {
        this.locationTimezone = await getTimezoneFromLocationString(this.location, VUE_GOOGLEMAP_API_KEY);
      } catch (error) {
        console.error("Failed to load location timezone:", error);
      }
    }
  }

  beforeDestroy() {
    window.removeEventListener("resize", this.checkTruncation); // Clean up listener
  }

  updated() {
    this.checkTruncation();
  }

  checkTruncation() {
    // Refers to the element containing the title
    const element = this.$refs.titleElement as HTMLElement;
    if (element) {
      this.isTruncated = element.scrollWidth > element.clientWidth;
    }
  }

  get location(): string {
    if (this.activity.location !== null) {
      const address = this.activity.location;
      return address.trim();
    }
  }

  get skillIds(): number[] {
    return this.requestSkills.map(({id}: Skill) => id);
  }

  get requestSkills(): Skill[] {
    return this.activity.project_activity_skills?.map(({skill}) => skill);
  }

  get excludeResourceUserId(): string {
    return this.activity?.contractor?.user.id;
  }

  get assignedOn(): string {
    if (this.activity?.contractor_updated_at) {
      const date = moment(this.activity?.contractor_updated_at).format(
          "DD MMM, YYYY"
      );
      return `${date}`;
    } else {
      return "n/a";
    }
  }

  getVisitType(type: string | undefined): string {
    if (!type) return "n/a";
    return (
        visitTypeDisplayMap[type] ||
        type
            .replace(/_/g, " ")
            .toLowerCase()
            .replace(/(^\w|\s\w)/g, (m) => m.toUpperCase())
    );
  }

  get alertType(): string {
    if (this.isOffered && !this.noAssignee)
      return "Awaiting resource response";
    if (this.isOffered && this.noAssignee) return "Missing assignee";
    if (this.isDeclined) return "Missing assignee";
    if (this.isDrafted) return "Publish visit";
  }

  get alertMessage(): string {
    if (this.isOffered && !this.noAssignee)
      return "Assignee has not yet confirmed this visit";
    if (this.isOffered && this.noAssignee)
      return "Please add an assignee to this visit";
    if (this.isDeclined) return "This visit is empty, please add an assignee";
    if (this.isDrafted) return "Please publish this task for assignee to view";
  }

  get visitType(): string {
    return this.getVisitType(this.activity?.type_of_visit);
  }

  get googleMapsLink(): string {
    const baseMapsUrl = "https://www.google.com/maps/search/?api=1";
    const queryParam = `query=${encodeURIComponent(this.location)}`;
    return this.location ? `${baseMapsUrl}&${queryParam}` : "";
  }

  get isAdmin(): boolean {
    return this.currentUser.role === Roles.ADMIN;
  }

  get managerRoute() {
    return {
      name: RouteNames.MANAGER_ABOUT,
      params: {
        id: this.activity?.contractor?.id,
      },
    };
  }

  async projectDetails() {
    const clientId = await this.fetchClientId();
    this.$router.push({
      name: RouteNames.PROJECT_ACTIVITY,
      params: {
        id: clientId,
      },
    });
  }

  visitUpdated(): void {
    this.$emit("visitUpdated");
  }

  get initial(): string {
    const firstName = this.activity?.contractor.user?.given_name;
    const lastName = this.activity?.contractor.user?.family_name;

    if (!firstName && !lastName) {
      return "";
    }

    const givenName = firstName ? firstName.trimLeft()[0] : "";
    const familyName = lastName ? lastName.trimLeft()[0] : "";
    return `${givenName}${familyName}`;
  }

  get bgStyle(): StyleValue {
    return {
      background: hslColorFromName(this.initial || ""),
    };
  }

  get isCurrentUserCreator(): boolean {
    return this.activity?.created_by === this.currentUser?.id;
  }

  get isOffered(): boolean {
    return (
        this.activity.accepted === null &&
        this.activity.drafted === false &&
        this.activity.completed === false
    );
  }

  get noAssignee(): boolean {
    return (
        !Boolean(this.activity?.contractor_id) ||
        !Boolean(this.activity.contractor)
    );
  }

  get isDeclined(): boolean {
    return (
        this.activity.accepted === false &&
        this.activity.drafted === false &&
        this.activity.completed === false
    );
  }

  get isAccepted(): boolean {
    return (
        this.activity.accepted === true &&
        this.activity.drafted === false &&
        this.activity.completed === false
    );
  }

  get isDrafted(): boolean {
    return (
        this.activity.drafted === true &&
        this.activity.accepted === null &&
        this.activity.completed === false
    );
  }

  get startDate(): string {
    const startDate = this.$moment(this.activity.start_time) as unknown as Moment;
    return startDate.format("DD MMM, YYYY");
  }

  get startTime(): string {
    if (!this.activity?.start_time || !this.locationTimezone) {
      return "n/a";
    }

    try {
      return displayTimeInLocationTimezone(this.activity.start_time, this.locationTimezone, "h:mm A");
    } catch (error) {
      return "Invalid Time";
    }
  }

  get endTime(): string {
    if (!this.activity?.end_time || !this.locationTimezone) {
      return "n/a";
    }

    try {
      return displayTimeInLocationTimezone(this.activity.end_time, this.locationTimezone, "h:mm A");
    } catch (error) {
      return "Invalid Time";
    }
  }

  get filteredDeclinedMatches(): any[] {
    // Database already filters out the assigned contractor and limits to 5
    const matches = this.declinedMatches || [];
    return matches.slice(0, 5);
  }

  get filteredBookmarkedContractors(): any[] {
    let contractors = this.bookmarkedContractors || [];

    // Filter out the currently assigned contractor
    const assignedContractorUserId = this.activity?.contractor?.user?.id;
    if (assignedContractorUserId) {
      contractors = contractors.filter(contractor => contractor.user_id !== assignedContractorUserId);
    }

    // Limit to maximum of 5 results
    console.log(contractors)
    return contractors.slice(0, 5);
  }

  getMatchBadgeClass(percentage: number): string {
    if (percentage >= 80) {
      return 'bg-green-100 text-green-800 hover:bg-green-100';
    } else if (percentage >= 70) {
      return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100';
    } else {
      return 'bg-red-100 text-red-800 hover:bg-red-100';
    }
  }

  handleContractorClick(user: any): void {

    // Emit an event to the parent to open the modal with suggested assignee
    this.$emit('openActivityModal', {
      projectActivity: this.activity,
      suggestedAssignee: user
    });
  }

  openSmartMatchModal(): void {
    this.showSmartMatchModal = true;
  }

  closeSmartMatchModal(): void {
    this.showSmartMatchModal = false;
  }
}
